import {
  transcriptState,
  isPremiumState,
  videoProgressState,
  settingsState,
} from '@/store'
import { FileText, HelpCircle } from 'react-feather'
import { useState } from 'react'
import { useRecoilState } from 'recoil'
import api from '@/utils/api'
import analytics from '@/utils/analytics'
import { getYouTubeID, hideAllTooltips } from '@/utils/index'
import toast from 'react-hot-toast'
import Button from '@/components/Button'
import ReactTooltip from 'react-tooltip'
import { useShowTranscriptButtonModal } from '@/components/Modal'

const SECONDS_TO_FIND = 15

export default function TranscriptButton({
  content,
  onContentChange,
  videoUrl,
  setCurrentView,
  isDemo,
  className,
}) {
  const [isLoading, setIsloading] = useState(false)
  const [transcript, setTranscript] = useRecoilState(transcriptState)
  const [isPremium] = useRecoilState(isPremiumState)
  const [progress] = useRecoilState(videoProgressState)
  const [settings] = useRecoilState(settingsState)
  const showTranscriptModal = useShowTranscriptButtonModal()

  async function loadTranscript() {
    if (transcript) {
      return transcript
    }

    const language = (settings && settings.transcriptLanguage) || 'en'
    const ytVideoID = getYouTubeID(videoUrl)
    const response = await api.get(`/transcript/${ytVideoID}?lang=${language}`)
    const loadedTranscript = response.data
    setTranscript(loadedTranscript)
    return loadedTranscript
  }

  async function onTranscriptClick() {
    hideAllTooltips()

    if (!isPremium && !isDemo) {
      analytics.track('(Transcript Button) Not Premium')
      showTranscriptModal()
      return
    }

    const currentProgress = progress.playedSeconds || 0

    try {
      setIsloading(true)
      analytics.track('Transcript button clicked')
      const loadedTranscript = await loadTranscript()

      if (loadedTranscript.length === 0) {
        toast.error(
          "We couldn't generate transcripts for this video or language"
        )
        setIsloading(false)
        return
      }

      const lastTranscript = getLastSecondsTranscript({
        currentProgress,
        transcript: loadedTranscript,
      })
      copyToEditor(lastTranscript)
      setIsloading(false)
    } catch (error) {
      toast.error("We couldn't generate transcripts for this video or language")
      analytics.track('Error loading transcript')
      setTranscript([])
    } finally {
      setIsloading(false)
    }
  }

  function getLastSecondsTranscript({ currentProgress, transcript }) {
    let lastTranscript = ''
    let found = false
    const before = currentProgress - SECONDS_TO_FIND
    const after = currentProgress + SECONDS_TO_FIND

    for (let index = 0; index < transcript.length; index++) {
      const item = transcript[index]
      const itemTime = +item.start

      if (itemTime >= before && itemTime <= after) {
        found = true
        lastTranscript += `${item.text.trim()} `
      } else if (found) {
        break
      }
    }

    return lastTranscript
  }

  function copyToEditor(text) {
    const newContent = content
      ? `${content} \n\n > ${text} \n\n`
      : `> ${text} \n\n`
    onContentChange({ text: newContent })
    setCurrentView('edit')
  }

  return (
    <>
      <Button
        data-tip
        data-for="transcript-button"
        isLoading={isLoading}
        onClick={onTranscriptClick}
        className={className}
        tabIndex={-1}
      >
        <FileText className="h-5 w-5" />
      </Button>

      <ReactTooltip
        place="top"
        effect="solid"
        id="transcript-button"
        clickable
        delayHide={100}
      >
        <span className="center">
          {`Transcript ${SECONDS_TO_FIND * 2}'' range${
            isPremium ? '' : ' (Premium)'
          }`}
          <HelpCircle
            onClick={() => {
              analytics.track('(Transcript) tooltip info clicked')
              showTranscriptModal()
            }}
            className="clickable-opacity ml-2 h-5 w-5"
          />
        </span>
      </ReactTooltip>
    </>
  )
}
